import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { BillingSyncChange, BillingSyncPreviewDTO } from "@shaarp/common";
import { ArrowRightIcon, Loader2Icon } from "lucide-react";
import { useState } from "react";

type Props = {
  teamId: string;
  push: boolean;
  pull: boolean;
  /** Mirrors the "Enable/Disable and sync" action: flips billingEnabled in the preview. */
  toggleBilling?: boolean;
  /** Overrides the default Push/Pull modal title (used by the toggle action). */
  title?: string;
  children: React.ReactNode;
  onConfirm: () => void;
};

/**
 * Menu item that, before applying a Push/Pull Stripe sync, opens a modal
 * previewing the exact field-level changes it would apply in Shaarp and in
 * Stripe (fetched via a backend dry-run).
 */
export function DialogAdminTeamBillingSyncConfirm({
  teamId,
  push,
  pull,
  toggleBilling = false,
  title,
  children,
  onConfirm,
}: Props) {
  const { t } = useTranslation();
  const api = useApi();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [preview, setPreview] = useState<BillingSyncPreviewDTO | null>(null);

  const loadPreview = async () => {
    setLoading(true);
    setError(false);
    setPreview(null);
    try {
      const { data } = await api.get<BillingSyncPreviewDTO>(
        `/admin/team/${teamId}/billing/sync/preview`,
        { params: { toggleBilling, push, pull } },
      );
      setPreview(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const doOpen = () => {
    setOpen(true);
    loadPreview();
  };

  const doConfirm = () => {
    setOpen(false);
    onConfirm();
  };

  return (
    <>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          doOpen();
        }}
      >
        {children}
      </DropdownMenuItem>

      <AlertDialog open={open}>
        <AlertDialogContent className="max-h-[85vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {title ??
                (push
                  ? t(
                      "admin.dataTableAdminTeams.billingMenu.syncPreview.pushTitle",
                    )
                  : t(
                      "admin.dataTableAdminTeams.billingMenu.syncPreview.pullTitle",
                    ))}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.dataTableAdminTeams.billingMenu.syncPreview.intro")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex-1 overflow-auto text-sm">
            {loading && (
              <div className="flex items-center gap-2 text-muted-foreground py-4">
                <Loader2Icon className="w-4 h-4 animate-spin" />
                {t(
                  "admin.dataTableAdminTeams.billingMenu.syncPreview.loading",
                )}
              </div>
            )}

            {!loading && error && (
              <div className="text-destructive py-4">
                {t("admin.dataTableAdminTeams.billingMenu.syncPreview.error")}
              </div>
            )}

            {!loading && !error && preview && (
              <div className="flex flex-col gap-4">
                {!preview.hasChanges && (
                  <div className="text-muted-foreground py-2">
                    {t(
                      "admin.dataTableAdminTeams.billingMenu.syncPreview.empty",
                    )}
                  </div>
                )}

                <ChangeSection
                  title={t(
                    "admin.dataTableAdminTeams.billingMenu.syncPreview.shaarpChanges",
                  )}
                  emptyLabel={t(
                    "admin.dataTableAdminTeams.billingMenu.syncPreview.noShaarpChanges",
                  )}
                  emptyValue={t(
                    "admin.dataTableAdminTeams.billingMenu.syncPreview.emptyValue",
                  )}
                  changes={preview.shaarpChanges}
                />

                <ChangeSection
                  title={t(
                    "admin.dataTableAdminTeams.billingMenu.syncPreview.stripeChanges",
                  )}
                  emptyLabel={t(
                    "admin.dataTableAdminTeams.billingMenu.syncPreview.noStripeChanges",
                  )}
                  emptyValue={t(
                    "admin.dataTableAdminTeams.billingMenu.syncPreview.emptyValue",
                  )}
                  changes={preview.stripeChanges}
                />
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setOpen(false)}>
              {t("admin.dataTableAdminTeams.billingMenu.syncPreview.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={doConfirm} disabled={loading || error}>
              {t("admin.dataTableAdminTeams.billingMenu.syncPreview.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ChangeSection({
  title,
  emptyLabel,
  emptyValue,
  changes,
}: {
  title: string;
  emptyLabel: string;
  emptyValue: string;
  changes: BillingSyncChange[];
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <div className="font-semibold">{title}</div>
      {changes.length === 0 ? (
        <div className="text-muted-foreground">{emptyLabel}</div>
      ) : (
        <ul className="flex flex-col gap-1">
          {changes.map((change) => (
            <li
              key={change.field}
              className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 px-2 py-1"
            >
              <span className="font-medium">
                {t(
                  `admin.dataTableAdminTeams.billingMenu.syncPreview.fields.${change.field}`,
                  { defaultValue: change.field },
                )}
              </span>
              <span className="flex items-center gap-1 text-xs font-mono">
                <span className="text-muted-foreground line-through">
                  {change.before ?? emptyValue}
                </span>
                <ArrowRightIcon className="w-3 h-3 shrink-0" />
                <span>{change.after ?? emptyValue}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
